import { useCallback, useState } from 'react'
import { apiFetch } from '../api/core'
import type { ConfigPreset, ConfigPresetMode } from '../types/testdata.types'

export function useConfigPresets() {
  const [presets, setPresets] = useState<ConfigPreset[]>([])
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadPresets = useCallback(async () => {
    try {
      const data = await apiFetch<ConfigPreset[]>('/api/testdata/config-presets')
      setPresets(data)
    } catch { /* niet kritisch */ }
  }, [])

  const savePreset = async (mode: ConfigPresetMode, configJson: Record<string, unknown>) => {
    if (!name.trim()) {
      setError('Geef het testscenario een naam.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const created = await apiFetch<ConfigPreset>('/api/testdata/config-presets', {
        method: 'POST',
        body: JSON.stringify({ name, mode, config_json: configJson }),
      })
      setPresets(ps => [created, ...ps])
      setName('')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Opslaan mislukt')
    } finally {
      setSaving(false)
    }
  }

  const deletePreset = async (id: number) => {
    setDeletingId(id)
    try {
      await apiFetch(`/api/testdata/config-presets/${id}`, { method: 'DELETE' })
      setPresets(ps => ps.filter(p => p.id !== id))
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Verwijderen mislukt')
    } finally {
      setDeletingId(null)
    }
  }

  return {
    presets, loadPresets,
    name, setName,
    saving, savePreset,
    deletingId, deletePreset,
    error, setError,
  }
}

export type UseConfigPresetsReturn = ReturnType<typeof useConfigPresets>
